import { HttpClient, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { PageRequest } from 'app/shared/models/request/page-request.model';
import { PageResponse } from '@iapmoit/library.angular.core';
import { environment } from 'environments/environment';
import { BehaviorSubject, forkJoin, Observable, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { AuditDocumentationRequirement, Country, ExpenseReportOption, IndustryCode, IndustryCodesToStandard, IndustryCompetency, ListApplicationStatus, ListAuditPackage, ListAuditStatus, ListAuditType, ListCertificateStatus, ListCertificationType, ListClientStatus, ListLanguage, ListLegalStatus, ListPreviousAuditReviewStatus, ListQuoteStatus, ListQuoteType, ListRiskLevel, ListSalesResponsible, ListSponsorLocation, QuoteRequestStatus, SalesQuoteRule, SalesQuoteRuleSet, SalesQuoteRuleSetPhase, SalesQuoteRuleSetStandard, StandardProfile, StandardProfileLineItem, StandardProfileRate, StateProvince, TestQuestion, TestQuestionAnswer, EmailTemplate } from 'app/shared/models/domain/data.model';
import { StandardSettingView } from './standards/standards.types';
import { SalesQuoteRuleSetListView, SalesQuoteRuleSetPhaseView, SalesQuoteRuleSetStandardView, SalesQuoteRuleSetView } from './sales-quotes/sales-quotes.types';
import { SearchOptionRequest } from 'app/shared/models/request/search-option-request.model';
import { TestsView } from 'app/shared/models/view/test-manager/tests-view.model';
import { TestDetailsView } from 'app/shared/models/view/test-manager/test-details-view.model';
import { StandardProfileView } from './standard-profile/models/standard-profile-view.model';

@Injectable({
    providedIn: 'root'
})
export class SettingsService {
    baseUrl = environment.apiBaseUrl;
    httpOptions: any = {
        withCredentials: true,
        observe: 'response' as const
    };
    constructor(
        private _httpClient: HttpClient,
    ) {
    }

    private sortMap: any = {
        created: 'createdDate'
    };
    private _standards: BehaviorSubject<PageResponse<StandardSettingView> | null> = new BehaviorSubject(null);
    private _clientStatus: BehaviorSubject<PageResponse<ListClientStatus> | null> = new BehaviorSubject(null);
    private _sponsorLocation: BehaviorSubject<PageResponse<ListSponsorLocation> | null> = new BehaviorSubject(null);
    private _expenseReportOptions: BehaviorSubject<PageResponse<ExpenseReportOption> | null> = new BehaviorSubject(null);
    private _salesResponsible: BehaviorSubject<PageResponse<ListSalesResponsible> | null> = new BehaviorSubject(null);
    private _industryCode: BehaviorSubject<PageResponse<IndustryCode> | null> = new BehaviorSubject(null);
    private _standardProfile: BehaviorSubject<PageResponse<StandardProfile> | null> = new BehaviorSubject(null);
    private _language: BehaviorSubject<PageResponse<ListLanguage> | null> = new BehaviorSubject(null);
    private _certificationType: BehaviorSubject<PageResponse<ListCertificationType> | null> = new BehaviorSubject(null);
    private _country: BehaviorSubject<PageResponse<Country> | null> = new BehaviorSubject(null);
    private _stateProvince: BehaviorSubject<PageResponse<StateProvince> | null> = new BehaviorSubject(null);
    private _applicationStatus: BehaviorSubject<PageResponse<ListApplicationStatus> | null> = new BehaviorSubject(null);
    private _quoteType: BehaviorSubject<PageResponse<ListQuoteType> | null> = new BehaviorSubject(null);
    private _quoteStatus: BehaviorSubject<PageResponse<ListQuoteStatus> | null> = new BehaviorSubject(null);
    private _auditPackage: BehaviorSubject<PageResponse<ListAuditPackage> | null> = new BehaviorSubject(null);
    private _riskLevel: BehaviorSubject<PageResponse<ListRiskLevel> | null> = new BehaviorSubject(null);
    private _listCertificateStatus: BehaviorSubject<PageResponse<ListCertificateStatus> | null> = new BehaviorSubject(null);
    private _auditType: BehaviorSubject<PageResponse<ListAuditType> | null> = new BehaviorSubject(null);
    private _auditStatus: BehaviorSubject<PageResponse<ListAuditStatus> | null> = new BehaviorSubject(null);
    private _auditDocumentRequirements: BehaviorSubject<PageResponse<AuditDocumentationRequirement> | null> = new BehaviorSubject(null);
    private _legalStatus: BehaviorSubject<PageResponse<ListLegalStatus> | null> = new BehaviorSubject(null);
    private _previousAuditReviewStatus: BehaviorSubject<PageResponse<ListPreviousAuditReviewStatus> | null> = new BehaviorSubject(null);
    private _previousQuoteRequestStatus: BehaviorSubject<PageResponse<QuoteRequestStatus> | null> = new BehaviorSubject(null);
    private readonly _emailTemplates: BehaviorSubject<PageResponse<EmailTemplate> | null> = new BehaviorSubject(null);

    get clients$(): Observable<PageResponse<StandardSettingView>> {
        return this._standards.asObservable();
    }

    getParams(request, params, doNotIgnoreDeleted = true): any {
        const normalizedRequest = {
            ...request,
            currentPage: request?.currentPage ?? 1,
            itemsPerPage: request?.itemsPerPage ?? environment.defaultItemsPerPage,
        };

        params['itemsPerPage'] = normalizedRequest.itemsPerPage.toString();
        params['currentPage'] = normalizedRequest.currentPage.toString();

        if (doNotIgnoreDeleted) {
            params['find[deleted]'] = 'IS NULL';
        }
        if (normalizedRequest.sort && normalizedRequest.sortDir) {
            if (Object.keys(this.sortMap).includes(normalizedRequest.sort)) {
                params[`sort[${this.sortMap[normalizedRequest.sort]}]`] = normalizedRequest.sortDir;
            }
            else {
                params[`sort[${normalizedRequest.sort}]`] = normalizedRequest.sortDir;
            }
        }

        if (normalizedRequest.query == 'standardId') {
            params['find[StandardId]'] = normalizedRequest.searchParam;
            params['find[applicationFormUrl]'] = 'IS NOT NULL';
        }

        if (normalizedRequest.test && normalizedRequest.test != '') {
            params['orLike[Title]'] = normalizedRequest.test;
            params['orLike[Slug]'] = normalizedRequest.test;
            params['orLike[IndustryScope]'] = normalizedRequest.test;
        }

        if (normalizedRequest.isExport) {
            params['fileType'] = normalizedRequest.exportType;
            params['excludedColumns'] = normalizedRequest.hideColumns;
        }

        if (normalizedRequest.countryList?.length) {
            params['find[CountryId:in]'] = normalizedRequest.countryList.join();
        }

        if (normalizedRequest.standardId) {
            params['find[standardId]'] = normalizedRequest.standardId;
        }

        return params;
    }

    searchRuleSet(query: string): Observable<any[]> {

        let params = {};
        if (query == '') {
            params = {};
        } else {
            params = {
                'orLike[Description]': query
                // , 'orLike[Email]': query
            };
        }

        const httpOptionsLocal = { ...this.httpOptions, params: params };
        return this._httpClient.get<any>(`${this.baseUrl}api/data/admin/sales-quote-rule-set`, httpOptionsLocal)
            .pipe(
                map((rulesets: HttpResponse<PageResponse<any>>) => {
                    return rulesets.body?.items;
                })
            );
    }

    getQuoteRuleSets(request: any): Observable<PageResponse<SalesQuoteRuleSetListView>> {
        let params = {};
        params = this.getParams(request, params);

        const httpOptionsLocal = { params: params };
        return this._httpClient.get<any>(this.baseUrl + 'api/data/admin/sales-quote-rule-set', httpOptionsLocal)
            .pipe(
                map((sets: any) => {

                    const ret: PageResponse<SalesQuoteRuleSetListView> = new PageResponse<SalesQuoteRuleSetListView>();

                    ret.items = sets.items.map((row: SalesQuoteRuleSet) => {
                        return {
                            ruleSetId: row.id
                            , complexity: row.complexity
                            , description: row.description
                            , notes: row.notes
                            , standards: null
                            , rules: 0
                            , phases: 0
                            , modified: row.modified || row.created
                        } as SalesQuoteRuleSetListView
                    });
                    ret.itemsPerPage = sets.itemsPerPage;
                    ret.totalItems = sets.totalItems;
                    ret.currentPage = sets.currentPage;

                    return ret;
                })
            );
    }

    getStandardsList(request: any): Observable<any> {
        let params = {};
        params = this.getParams(request, params);

        const httpOptionsLocal = { params: params };
        if (request.isExport) {
            return this._httpClient.get(this.baseUrl + 'api/data/standard/export', { params: params, responseType: 'arraybuffer' });
        }
        else {
            return this._httpClient.get<any>(this.baseUrl + 'api/data/standard', httpOptionsLocal)
                .pipe(
                    map((standards: any) => {
                        const ret: PageResponse<StandardSettingView> = new PageResponse<StandardSettingView>();
                        const returnData = standards?.items;

                        ret.items = returnData?.map((row) => {
                            return {
                                standardId: row.standardId
                                , standardName: row.standardName
                                , description: row.description
                                , active: row.active
                                , anabAccredited: row.anabAccredited || 0
                                , createdDate: row.dateAdded
                                , sortKey: row.sortKey
                                , dailyRate: row.dailyRate
                                , annualFee: row.annualFee
                                , calendarBackgroundColor: row.calendarBackgroundColor
                                , calendarTextColor: row.calendarTextColor
                                , applicationFormUrl: row.applicationFormUrl
                                , preAuditFormUrl: row.preAuditFormUrl
                                , profileSlug: row.profileSlug
                            } as StandardSettingView;
                            //return row as Standard;
                        });

                        ret.totalItems = standards.page?.totalItems;
                        ret.currentPage = request.currentPage;
                        ret.itemsPerPage = request.itemsPerPage;
                        this._standards.next(ret);
                        return ret;
                    })
                );
        }
    }

    getClientStatusList(request: any): Observable<PageResponse<ListClientStatus>> {
        let params = {};
        params = this.getParams(request, params);

        const httpOptionsLocal = { params: params };
        return this._httpClient.get<any>(this.baseUrl + 'api/data/admin/client-status', httpOptionsLocal)
            .pipe(
                map((clientStatus: any) => {
                    const ret: PageResponse<ListClientStatus> = new PageResponse<ListClientStatus>();
                    const returnData = clientStatus?.items;

                    ret.items = returnData?.map((row) => {
                        return {
                            clientStatus: row.clientStatus
                            , clientStatusId: row.clientStatusId
                        } as ListClientStatus;
                    });

                    ret.totalItems = clientStatus.page?.totalItems;
                    ret.currentPage = request.currentPage;
                    ret.itemsPerPage = request.itemsPerPage;
                    this._clientStatus.next(ret);
                    return ret;
                })
            );
    }

    getSponsorLocationList(request: any): Observable<PageResponse<ListSponsorLocation>> {
        let params = {};
        params = this.getParams(request, params);

        const httpOptionsLocal = { params: params };
        return this._httpClient.get<any>(this.baseUrl + 'api/data/sponsor-location', httpOptionsLocal)
            .pipe(
                map((sponsorLocation: any) => {
                    const ret: PageResponse<ListSponsorLocation> = new PageResponse<ListSponsorLocation>();
                    const returnData = sponsorLocation?.items;

                    ret.items = returnData?.map((row) => {
                        return {
                            sponsorLocation: row.sponsorLocation
                            , sponsorLocationId: row.sponsorLocationId
                        } as ListSponsorLocation;
                    });

                    ret.totalItems = sponsorLocation.page?.totalItems;
                    ret.currentPage = request.currentPage;
                    ret.itemsPerPage = request.itemsPerPage;
                    this._sponsorLocation.next(ret);
                    return ret;
                })
            );
    }

    getExpenseReportOptionsList(request: any): Observable<PageResponse<ExpenseReportOption>> {
        let params = {};
        params = this.getParams(request, params);

        const httpOptionsLocal = { params: params };
        return this._httpClient.get<any>(this.baseUrl + 'api/admin/expense-report-options', httpOptionsLocal)
            .pipe(
                map((sponsorLocation: any) => {
                    const ret: PageResponse<ExpenseReportOption> = new PageResponse<ExpenseReportOption>();
                    const returnData = sponsorLocation?.items;

                    ret.items = returnData?.map((row) => {
                        return {
                            title: row.title
                            , optionType: row.optionType
                            , unitPrice: row.unitPrice
                            , generated: row.generated
                            , descriptionRequired: row.descriptionRequired
                            , quantityRequired: row.quantityRequired
                            , mediaRequired: row.mediaRequired
                            , approvalRequired: row.approvalRequired
                            , ...row
                        } as ExpenseReportOption;
                    });

                    ret.totalItems = sponsorLocation.page?.totalItems;
                    ret.currentPage = request.currentPage;
                    ret.itemsPerPage = request.itemsPerPage;
                    this._expenseReportOptions.next(ret);
                    return ret;
                })
            );
    }

    getEmailTemplateList(request: any): Observable<PageResponse<EmailTemplate>> {
        let params = {};
        params = this.getParams(request, params, false);

        const httpOptionsLocal = { params: params };
        return this._httpClient.get<any>(this.baseUrl + 'api/data/admin/email-templates', httpOptionsLocal)
            .pipe(
                map((emailTemplate: any) => {
                    const ret: PageResponse<EmailTemplate> = new PageResponse<EmailTemplate>();
                    const returnData = emailTemplate?.items;

                    ret.items = returnData;

                    ret.totalItems = emailTemplate.page?.totalItems;
                    ret.currentPage = request.currentPage;
                    ret.itemsPerPage = request.itemsPerPage;
                    this._emailTemplates.next(ret);
                    return ret;
                })
            );
    }

    getAuditorExpenseReportOptionsList(request: any): Observable<PageResponse<ExpenseReportOption>> {
        let params = {};
        params = this.getParams(request, params, false);
        const httpOptionsLocal = { params: params };
        return this._httpClient.get<any>(this.baseUrl + 'api/auditor/expense-report-options', httpOptionsLocal)
            .pipe(
                map((sponsorLocation: any) => {
                    const ret: PageResponse<ExpenseReportOption> = new PageResponse<ExpenseReportOption>();
                    const returnData = sponsorLocation?.items;

                    ret.items = returnData?.map((row) => {
                        return {
                            title: row.title
                            , optionType: row.optionType
                            , unitPrice: row.unitPrice
                            , generated: row.generated
                            , descriptionRequired: row.descriptionRequired
                            , quantityRequired: row.quantityRequired
                            , mediaRequired: row.mediaRequired
                            , approvalRequired: row.approvalRequired
                            , ...row
                        } as ExpenseReportOption;
                    });

                    ret.totalItems = sponsorLocation.page?.totalItems;
                    ret.currentPage = request.currentPage;
                    ret.itemsPerPage = request.itemsPerPage;
                    this._expenseReportOptions.next(ret);
                    return ret;
                })
            );
    }

    getSalesResponsibleList(request: any): Observable<PageResponse<ListSalesResponsible>> {
        let params = {};
        params = this.getParams(request, params);

        const httpOptionsLocal = { params: params };
        return this._httpClient.get<any>(this.baseUrl + 'api/data/sales-responsible', httpOptionsLocal)
            .pipe(
                map((sponsorLocation: any) => {
                    const ret: PageResponse<ListSalesResponsible> = new PageResponse<ListSalesResponsible>();
                    const returnData = sponsorLocation?.items;

                    ret.items = returnData?.map((row) => {
                        return {
                            salesResponsible: row.salesResponsible
                            , salesResponsibleId: row.salesResponsibleId
                        } as ListSalesResponsible;
                    });

                    ret.totalItems = sponsorLocation.page?.totalItems;
                    ret.currentPage = request.currentPage;
                    ret.itemsPerPage = request.itemsPerPage;
                    this._salesResponsible.next(ret);
                    return ret;
                })
            );
    }

    getIndustryCodeList(request: any): Observable<PageResponse<IndustryCode>> {
        let params = {};
        params = this.getParams(request, params);

        const httpOptionsLocal = { params: params };
        return this._httpClient.get<any>(this.baseUrl + 'api/data/industry-code', httpOptionsLocal)
            .pipe(
                map((sponsorLocation: any) => {
                    const ret: PageResponse<IndustryCode> = new PageResponse<IndustryCode>();
                    const returnData = sponsorLocation?.items;

                    ret.items = returnData?.map((row) => {
                        return {
                            industryCodeId: row.industryCodeId
                            , industryScope: row.industryScope
                            , accredited: row.accredited
                            , industryCodeReferenceId: row.industryCodeReferenceId
                        } as IndustryCode;
                    });

                    ret.totalItems = sponsorLocation.page?.totalItems;
                    ret.currentPage = request.currentPage;
                    ret.itemsPerPage = request.itemsPerPage;
                    this._industryCode.next(ret);
                    return ret;
                })
            );
    }

    getStandardProfilesList(request: any): Observable<PageResponse<StandardProfile>> {
        let params = {};
        this.sortMap['created'] = 'created';
        params = this.getParams(request, params, false);

        const httpOptionsLocal = { params: params };
        return this._httpClient.get<any>(this.baseUrl + 'api/data/admin/standard-profile', httpOptionsLocal)
            .pipe(
                map((sponsorLocation: any) => {
                    const ret: PageResponse<StandardProfile> = new PageResponse<StandardProfile>();
                    const returnData = sponsorLocation?.items;
                    ret.items = returnData?.map((row) => {
                        return {
                            standardId: row.standardId,
                            name: row.name,
                            description: row.description,
                            ...row
                        } as StandardProfile;
                    });

                    ret.totalItems = sponsorLocation.page?.totalItems;
                    ret.currentPage = request.currentPage;
                    ret.itemsPerPage = request.itemsPerPage;
                    this._standardProfile.next(ret);
                    return ret;
                })
            );
    }

    getStandardProfileById(id: string): Observable<StandardProfile> {
        const roleType = 'admin';
        return this._httpClient.get<PageResponse<StandardProfile>>(`${this.baseUrl}api/data/${roleType}/standard-profile/${id}`, this.httpOptions).
            pipe(map((response: any) => {
                return response?.body?.item;
            }));
    }

    getStandardProfileLineItemsByProfileId(salesQuoteProfileId: string): Observable<StandardProfileLineItem[]> {
        return this._httpClient.get<any>(`${this.baseUrl}api/data/admin/standard-profile-line-items`, {
            params: {
                'find[StandardProfileId]': salesQuoteProfileId,
                'itemsPerPage': 10000,
                'sort[Employees]': 'desc',
            }
        }).pipe(map((data) => {
            return data.items?.map((rows) => {
                return {
                    employees: rows.employees,
                    initialAuditDays: rows.initialAuditDays,
                    ...rows
                };
            });
        }));
    }

    getStandardProfileRatesByProfileId(salesQuoteProfileId: string): Observable<StandardProfileRate[]> {
        return this._httpClient.get<any>(`${this.baseUrl}api/data/admin/standard-profile-rates`, {
            params: {
                'find[StandardProfileId]': salesQuoteProfileId,
                'itemsPerPage': 10000,
                'sort[Year]': 'desc',
            }
        }).pipe(map((data) => {
            return data.items?.map((rows) => {
                return {
                    year: rows.year,
                    dailyRate: rows.dailyRate,
                    ...rows
                };
            });
        }));
    }

    getStandardProfilesView(id: string): Observable<StandardProfileView> {
        const tasks = [
            this.getStandardProfileById(id),
            this.getStandardProfileLineItemsByProfileId(id),
            this.getStandardProfileRatesByProfileId(id)
        ];
        return forkJoin(tasks)
            .pipe(
                switchMap(([standardProfile, standardProfileLineItems, standardProfileRates]:
                    [StandardProfile, StandardProfileLineItem[], StandardProfileRate[]]) => {
                    return of({
                        standardProfile: standardProfile,
                        standardProfileLineItems: standardProfileLineItems,
                        standardProfileRates: standardProfileRates
                    } as StandardProfileView);
                })
            );
    }

    getLanguageList(request: any): Observable<PageResponse<ListLanguage>> {
        let params = {};
        params = this.getParams(request, params);

        const httpOptionsLocal = { params: params };
        return this._httpClient.get<any>(this.baseUrl + 'api/data/language', httpOptionsLocal)
            .pipe(
                map((language: any) => {
                    const ret: PageResponse<ListLanguage> = new PageResponse<ListLanguage>();
                    const returnData = language?.items;

                    ret.items = returnData?.map((row) => {
                        return {
                            language: row.language
                            , languageId: row.languageId
                        } as ListLanguage;
                    });

                    ret.totalItems = language.page?.totalItems;
                    ret.currentPage = request.currentPage;
                    ret.itemsPerPage = request.itemsPerPage;
                    this._language.next(ret);
                    return ret;
                })
            );
    }

    getCertificationTypeList(request: any): Observable<PageResponse<ListCertificationType>> {
        let params = {};
        params = this.getParams(request, params);

        const httpOptionsLocal = { params: params };
        return this._httpClient.get<any>(this.baseUrl + 'api/data/certification-type', httpOptionsLocal)
            .pipe(
                map((certificationType: any) => {
                    const ret: PageResponse<ListCertificationType> = new PageResponse<ListCertificationType>();
                    const returnData = certificationType?.items;

                    ret.items = returnData?.map((row) => {
                        return {
                            certificationType: row.certificationType
                            , certificationTypeId: row.certificationTypeId
                        } as ListCertificationType;
                    });

                    ret.totalItems = certificationType.page?.totalItems;
                    ret.currentPage = request.currentPage;
                    ret.itemsPerPage = request.itemsPerPage;
                    this._certificationType.next(ret);
                    return ret;
                })
            );
    }

    getCountryList(request: any): Observable<PageResponse<Country>> {
        let params = {};
        params = this.getParams(request, params);

        const httpOptionsLocal = { params: params };
        return this._httpClient.get<any>(this.baseUrl + 'api/data/country', httpOptionsLocal)
            .pipe(
                map((country: any) => {
                    const ret: PageResponse<Country> = new PageResponse<Country>();
                    const returnData = country?.items;

                    ret.items = returnData?.map((row) => {
                        return {
                            countryName: row.countryName
                            , countryId: row.countryId
                            , sortKey: row.sortKey
                            , abbreviation: row.abbreviation
                        } as Country;
                    });

                    ret.totalItems = country.page?.totalItems;
                    ret.currentPage = request.currentPage;
                    ret.itemsPerPage = request.itemsPerPage;
                    this._country.next(ret);
                    return ret;
                })
            );
    }

    getStateProvinceList(request: any): Observable<PageResponse<StateProvince>> {
        let params = {};
        params = this.getParams(request, params);

        const httpOptionsLocal = { params: params };
        return this._httpClient.get<any>(this.baseUrl + 'api/data/state-province', httpOptionsLocal)
            .pipe(
                map((stateProvince: any) => {
                    const ret: PageResponse<StateProvince> = new PageResponse<StateProvince>();
                    const returnData = stateProvince?.items;

                    ret.items = returnData?.map((row) => {
                        return {
                            abbreviation: row.abbreviation
                            , stateProvinceName: row.stateProvinceName
                            , stateProvinceId: row.stateProvinceId
                            , countryId: row.countryId
                        } as StateProvince;
                    });

                    ret.totalItems = stateProvince.page?.totalItems;
                    ret.currentPage = request.currentPage;
                    ret.itemsPerPage = request.itemsPerPage;
                    this._stateProvince.next(ret);
                    return ret;
                })
            );
    }

    getRulesByPhaseId(phaseId: string): Observable<SalesQuoteRule[]> {
        return this._httpClient.get<any>(this.baseUrl + 'api/data/admin/sales-quote-rule', {
            params: {
                'find[RuleSetPhaseId]': phaseId
                , 'sort[EmployeesMax]': 'asc'
                , 'currentPage': 1
                , 'itemsPerPage': 100
            }
        })
            .pipe(map((data: any) => (data.items)))
    }
    getRuleSetView(ruleSetId: string): Observable<SalesQuoteRuleSetView> {

        return forkJoin([
            this._httpClient.get<any>(this.baseUrl + 'api/data/admin/sales-quote-rule-set/' + ruleSetId, this.httpOptions)
                .pipe(map((data: HttpResponse<any>) => (data.body.item)))
            , this._httpClient.get<any>(this.baseUrl + 'api/data/admin/sales-quote-rule-set-phase', { params: { 'find[RuleSetId]': ruleSetId, 'sort[SortOrder]': 'asc' } })
                .pipe(map((data: any) => (data.items)))
            , this._httpClient.get<any>(this.baseUrl + 'api/data/admin/sales-quote-rule-set-standard', { params: { 'find[RuleSetId]': ruleSetId } })
                .pipe(map((data: any) => (data.items)))
            , this._httpClient.get<any>(this.baseUrl + 'api/public/options/get-options')
                .pipe(map((data) => {
                    return data.item.standardList;
                }))
        ]).pipe(
            switchMap(([set, phases, standardMap, standards]: [SalesQuoteRuleSetView, SalesQuoteRuleSetPhase[], SalesQuoteRuleSetStandard[], any]) => {

                if (phases?.length) {
                    const ruleTasks = phases.map(p => (this.getRulesByPhaseId(p.id)));

                    return forkJoin(ruleTasks)
                        .pipe(map((rules) => {
                            return this.processResult(set, phases, standardMap, standards, rules)
                        }))
                }

                return of(this.processResult(set, phases, standardMap, standards, []));
            })
        );
    }

    processResult(set: SalesQuoteRuleSetView, phases: SalesQuoteRuleSetPhase[], standardMap, standards, rules) {

        const ret = set as SalesQuoteRuleSetView;
        ret.phases = phases.map((row: SalesQuoteRuleSetPhase) => {
            const ret = row as SalesQuoteRuleSetPhaseView;
            ret.rules = rules[phases.findIndex(p => p.id == row.id)] as any;
            return ret;
        });

        const ruleMap = {};

        for (let o: number = 0; o < rules.length; o++) {
            for (let i: number = 0; i < rules[o].length; i++) {
                const key: string = `${rules[o][i].employeesMin}-${rules[o][i].employeesMax}`;

                if (!ruleMap[key]) {
                    ruleMap[key] = [key];
                }

                ruleMap[key].push(rules[o][i].durationDays);
            }
        }

        ret.ruleRows = Object.values(ruleMap);
        ret.standards = standardMap.map((std) => {
            const ret = std as SalesQuoteRuleSetStandardView;
            ret.standardName = standards.filter(s => s?.value == std?.standardId?.toString())[0]?.label;
            return ret;
        });

        return ret;
    }

    saveRule(rule: SalesQuoteRule): Observable<SalesQuoteRule> {
        if (rule.id) {
            return this._httpClient.put<any>(`${this.baseUrl}api/data/admin/sales-quote-rule/${rule.id}`, rule).pipe(map(data => data.item));
        } else {
            return this._httpClient.post<any>(`${this.baseUrl}api/data/admin/sales-quote-rule`, rule).pipe(map(data => data.item));
        }
    }

    saveStandardProfile(standardProfile: StandardProfile): Observable<StandardProfile> {
        if (standardProfile.id) {
            return this._httpClient.put<any>(`${this.baseUrl}api/data/admin/standard-profile/${standardProfile.id}`, standardProfile).pipe(map(data => data.item));
        } else {
            return this._httpClient.post<any>(`${this.baseUrl}api/data/admin/standard-profile`, standardProfile).pipe(map(data => data.item));
        }
    }

    getApplicationStatusList(request: any): Observable<PageResponse<ListApplicationStatus>> {
        let params = {};
        params = this.getParams(request, params);

        const httpOptionsLocal = { params: params };
        return this._httpClient.get<any>(this.baseUrl + 'api/data/application-status', httpOptionsLocal)
            .pipe(
                map((applicationStatus: any) => {
                    const ret: PageResponse<ListApplicationStatus> = new PageResponse<ListApplicationStatus>();
                    const returnData = applicationStatus?.items;

                    ret.items = returnData?.map((row) => {
                        return {
                            applicationStatus: row.applicationStatus
                            , applicationStatusId: row.applicationStatusId
                        } as ListApplicationStatus;
                    });

                    ret.totalItems = applicationStatus.page?.totalItems;
                    ret.currentPage = request.currentPage;
                    ret.itemsPerPage = request.itemsPerPage;
                    this._applicationStatus.next(ret);
                    return ret;
                })
            );
    }

    getQuoteTypeList(request: any): Observable<PageResponse<ListQuoteType>> {
        let params = {};
        params = this.getParams(request, params);

        const httpOptionsLocal = { params: params };
        return this._httpClient.get<any>(this.baseUrl + 'api/data/quote-type', httpOptionsLocal)
            .pipe(
                map((quoteType: any) => {
                    const ret: PageResponse<ListQuoteType> = new PageResponse<ListQuoteType>();
                    const returnData = quoteType?.items;

                    ret.items = returnData?.map((row) => {
                        return {
                            quoteType: row.quoteType
                            , quoteTypeId: row.quoteTypeId
                        } as ListQuoteType;
                    });

                    ret.totalItems = quoteType.page?.totalItems;
                    ret.currentPage = request.currentPage;
                    ret.itemsPerPage = request.itemsPerPage;
                    this._quoteType.next(ret);
                    return ret;
                })
            );
    }

    getQuoteStatusList(request: any): Observable<PageResponse<ListQuoteStatus>> {
        let params = {};
        params = this.getParams(request, params);

        const httpOptionsLocal = { params: params };
        return this._httpClient.get<any>(this.baseUrl + 'api/data/quote-status', httpOptionsLocal)
            .pipe(
                map((quoteStatus: any) => {
                    const ret: PageResponse<ListQuoteStatus> = new PageResponse<ListQuoteStatus>();
                    const returnData = quoteStatus?.items;

                    ret.items = returnData?.map((row) => {
                        return {
                            quoteStatus: row.quoteStatus
                            , quoteStatusId: row.quoteStatusId
                        } as ListQuoteStatus;
                    });

                    ret.totalItems = quoteStatus.page?.totalItems;
                    ret.currentPage = request.currentPage;
                    ret.itemsPerPage = request.itemsPerPage;
                    this._quoteStatus.next(ret);
                    return ret;
                })
            );
    }

    getAuditPackageList(request: any): Observable<PageResponse<ListAuditPackage>> {
        let params = {};
        params = this.getParams(request, params);

        const httpOptionsLocal = { params: params };
        return this._httpClient.get<any>(this.baseUrl + 'api/data/audit-package', httpOptionsLocal)
            .pipe(
                map((auditPackage: any) => {
                    const ret: PageResponse<ListAuditPackage> = new PageResponse<ListAuditPackage>();
                    const returnData = auditPackage?.items;

                    ret.items = returnData?.map((row) => {
                        return {
                            auditPackage: row.auditPackage
                            , auditPackageId: row.auditPackageId
                        } as ListAuditPackage;
                    });

                    ret.totalItems = auditPackage.page?.totalItems;
                    ret.currentPage = request.currentPage;
                    ret.itemsPerPage = request.itemsPerPage;
                    this._auditPackage.next(ret);
                    return ret;
                })
            );
    }

    getRiskLevelList(request: any): Observable<PageResponse<ListRiskLevel>> {
        let params = {};
        params = this.getParams(request, params);

        const httpOptionsLocal = { params: params };
        return this._httpClient.get<any>(this.baseUrl + 'api/data/risk-level', httpOptionsLocal)
            .pipe(
                map((riskLevel: any) => {
                    const ret: PageResponse<ListRiskLevel> = new PageResponse<ListRiskLevel>();
                    const returnData = riskLevel?.items;

                    ret.items = returnData?.map((row) => {
                        return {
                            riskLevel: row.riskLevel
                            , riskLevelId: row.riskLevelId
                        } as ListRiskLevel;
                    });

                    ret.totalItems = riskLevel.page?.totalItems;
                    ret.currentPage = request.currentPage;
                    ret.itemsPerPage = request.itemsPerPage;
                    this._riskLevel.next(ret);
                    return ret;
                })
            );
    }

    getCertificateStatusList(request: any): Observable<PageResponse<ListCertificateStatus>> {
        let params = {};
        params = this.getParams(request, params);

        const httpOptionsLocal = { params: params };
        return this._httpClient.get<any>(this.baseUrl + 'api/data/certificate-status', httpOptionsLocal)
            .pipe(
                map((listCertificateStatus: any) => {
                    const ret: PageResponse<ListCertificateStatus> = new PageResponse<ListCertificateStatus>();
                    const returnData = listCertificateStatus?.items;

                    ret.items = returnData?.map((row) => {
                        return {
                            certificateStatus: row.certificateStatus
                            , certificateStatusId: row.certificateStatusId
                        } as ListCertificateStatus;
                    });

                    ret.totalItems = listCertificateStatus.page?.totalItems;
                    ret.currentPage = request.currentPage;
                    ret.itemsPerPage = request.itemsPerPage;
                    this._listCertificateStatus.next(ret);
                    return ret;
                })
            );
    }

    getAuditTypeList(request: any): Observable<PageResponse<ListAuditType>> {
        let params = {};
        params = this.getParams(request, params);

        const httpOptionsLocal = { params: params };
        return this._httpClient.get<any>(this.baseUrl + 'api/data/audit-type', httpOptionsLocal)
            .pipe(
                map((auditType: any) => {
                    const ret: PageResponse<ListAuditType> = new PageResponse<ListAuditType>();
                    const returnData = auditType?.items;

                    ret.items = returnData?.map((row) => {
                        return {
                            auditType: row.auditType
                            , auditTypeId: row.auditTypeId
                            , calendarBackgroundColor: row.calendarBackgroundColor
                            , calendarTextColor: row.calendarTextColor
                            , sortOrder: row.sortOrder
                        } as ListAuditType;
                    });

                    ret.totalItems = auditType.page?.totalItems;
                    ret.currentPage = request.currentPage;
                    ret.itemsPerPage = request.itemsPerPage;
                    this._auditType.next(ret);
                    return ret;
                })
            );
    }

    getAuditStatusList(request: any): Observable<PageResponse<ListAuditStatus>> {
        let params = {};
        params = this.getParams(request, params);

        const httpOptionsLocal = { params: params };
        return this._httpClient.get<any>(this.baseUrl + 'api/data/audit-status', httpOptionsLocal)
            .pipe(
                map((auditType: any) => {
                    const ret: PageResponse<ListAuditStatus> = new PageResponse<ListAuditStatus>();
                    const returnData = auditType?.items;

                    ret.items = returnData?.map((row) => {
                        return {
                            auditStatus: row.auditStatus
                            , auditStatusId: row.auditStatusId
                            , calendarBackgroundColor: row.calendarBackgroundColor
                            , calendarTextColor: row.calendarTextColor
                        } as ListAuditStatus;
                    });

                    ret.totalItems = auditType.page?.totalItems;
                    ret.currentPage = request.currentPage;
                    ret.itemsPerPage = request.itemsPerPage;
                    this._auditStatus.next(ret);
                    return ret;
                })
            );
    }

    getAuditDocumentRequirementsList(request: any): Observable<PageResponse<AuditDocumentationRequirement>> {
        let params = {};
        params = this.getParams(request, params, false);

        const httpOptionsLocal = { params: params };
        return this._httpClient.get<any>(this.baseUrl + 'api/data/audit-documentation-requirements', httpOptionsLocal)
            .pipe(
                map((auditType: any) => {
                    const ret: PageResponse<AuditDocumentationRequirement> = new PageResponse<AuditDocumentationRequirement>();
                    const returnData = auditType?.items;

                    ret.items = returnData?.map((row) => {
                        return row;
                    });

                    ret.totalItems = auditType.page?.totalItems;
                    ret.currentPage = request.currentPage;
                    ret.itemsPerPage = request.itemsPerPage;
                    this._auditDocumentRequirements.next(ret);
                    return ret;
                })
            );
    }

    getIndustryCompetenciesList(request): Observable<PageResponse<IndustryCompetency>> {
        let params = {};
        params = this.getParams(request, params);
        const httpOptionsLocal = { params: params };
        httpOptionsLocal.params['itemsPerPage'] = 1000;
        return this._httpClient.get<any>(this.baseUrl + 'api/data/industry-competency', httpOptionsLocal)
            .pipe(
                map((industryCompetency) => {
                    const ret: PageResponse<IndustryCompetency> = new PageResponse<IndustryCompetency>();
                    const returnData = industryCompetency?.items;
                    ret.items = industryCompetency?.items;
                    ret.totalItems = industryCompetency.page?.totalItems;
                    ret.currentPage = request.currentPage;
                    ret.itemsPerPage = request.itemsPerPage;
                    return ret;
                })
            );
    }

    getIndustryCodesToStandardList(request): Observable<PageResponse<IndustryCodesToStandard>> {
        let params = {};
        params = this.getParams(request, params, false);
        const httpOptionsLocal = { params: params };
        return this._httpClient.get<any>(this.baseUrl + 'api/data/industry-codes-to-standard', httpOptionsLocal)
            .pipe(
                map((industryCompetency) => {
                    const ret: PageResponse<IndustryCodesToStandard> = new PageResponse<IndustryCodesToStandard>();
                    const returnData = industryCompetency?.items;
                    ret.items = industryCompetency?.items;
                    ret.totalItems = industryCompetency.page?.totalItems;
                    ret.currentPage = request.currentPage;
                    ret.itemsPerPage = request.itemsPerPage;
                    return ret;
                })
            );
    }

    getTestsList(request): Observable<PageResponse<TestsView>> {
        let params = {};
        params = this.getParams(request, params, false);
        const httpOptionsLocal = { params: params };
        return this._httpClient.get<any>(this.baseUrl + 'api/admin/view/tests', httpOptionsLocal)
            .pipe(
                map((industryCompetency) => {
                    const ret: PageResponse<TestsView> = new PageResponse<TestsView>();
                    const returnData = industryCompetency?.items;
                    ret.items = industryCompetency?.items;
                    ret.totalItems = industryCompetency.page?.totalItems;
                    ret.currentPage = request.currentPage;
                    ret.itemsPerPage = request.itemsPerPage;
                    return ret;
                })
            );
    }

    getTestQuestionAnswersByQuestionId(id): Observable<PageResponse<TestQuestionAnswer>> {
        return this._httpClient.get<PageResponse<TestQuestionAnswer>>(this.baseUrl + 'api/admin/test-question-answers', { params: { 'find[questionId]': id, 'itemsPerPage': 1000, 'sort[created]': 'asc' } })
            .pipe(map((data: any) => {
                const ret: PageResponse<TestQuestionAnswer> = data;
                return ret;
            }));
    }

    getTestById(id, evalId = ''): Observable<TestDetailsView> {
        return forkJoin([
            this._httpClient.get<any>(this.baseUrl + 'api/admin/view/tests', { params: { 'find[id]': id, 'itemsPerPage': 1 } }),
            this._httpClient.get<any>(this.baseUrl + 'api/admin/test-questions', { params: { 'find[testId]': id, 'find[deleted]': 'IS NULL', 'itemsPerPage': 1000, 'sort[sortOrder]': 'asc' } }),
            this._httpClient.get<any>(this.baseUrl + 'api/admin/test-question-answers', { params: { 'find[testId]': id, 'find[deleted]': 'IS NULL', 'itemsPerPage': 1000, 'sort[created]': 'asc' } }),
            // this._httpClient.get<any>(this.baseUrl + 'api/admin/view/test-question-answers', { params: { 'find[testId]': id, 'itemsPerPage': 1000 } }),
            (evalId && evalId != '' ? this._httpClient.get<any>(this.baseUrl + 'api/admin/auditor-eval-test-answers', { params: { 'find[auditorEvalTestId]': evalId, 'itemsPerPage': 1000, 'sort[created]': 'asc' } }) : of([])),
        ]).pipe(map((data: any) => {
            const questionView = [];
            data[1]?.items?.map((rows: TestQuestion, index) => {
                questionView.push({
                    order: index,
                    title: rows?.questionText,
                    subTitle: '',
                    question: rows,
                    answers: data[2]?.items?.filter(row => row.questionId == rows.id),
                    auditorAnswerId: data[3]?.items?.find(row => row.testQuestionId == rows.id)?.answerId,
                    auditorAnswerText: data[2]?.items?.filter(row => row.questionId == rows.id)?.
                        find(rowTemp => rowTemp.id == data[3]?.items?.find(row => row.testQuestionId == rows.id)?.answerId)?.answerText,
                });
            });

            return {
                test: data[0]?.items[0]
                , questions: questionView
                , answers: data[2]?.items
            } as TestDetailsView;
        }));
    }

    getIndustryCompetenciesOptionsList(standardIds: number[]): Observable<SearchOptionRequest[]> {
        const params = {};
        params['find[standardId:in'] = standardIds?.join(',')
        params['sort[sortOrder]'] = 'asc';
        const httpOptionsLocal = { params: params };
        return this._httpClient.get<PageResponse<IndustryCompetency>>(this.baseUrl + 'api/data/industry-competency', httpOptionsLocal)
            .pipe(
                map((response) => {
                    return response.items.map((item) => {
                        return {
                            label: `${item.prefix}.${item.sortOrder} ${item.industryCompetencyName}`,
                            value: item.id,
                            extra: item
                        } as SearchOptionRequest;
                    });
                })
            );
    }

    getLegalList(request: any): Observable<PageResponse<ListLegalStatus>> {
        let params = {};
        params = this.getParams(request, params);

        const httpOptionsLocal = { params: params };
        return this._httpClient.get<any>(this.baseUrl + 'api/data/legal-status', httpOptionsLocal)
            .pipe(
                map((auditType: any) => {
                    const ret: PageResponse<ListLegalStatus> = new PageResponse<ListLegalStatus>();
                    const returnData = auditType?.items;

                    ret.items = returnData?.map((row) => {
                        return {
                            legalStatus: row.legalStatus
                            , legalStatusId: row.legalStatusId
                        } as ListLegalStatus;
                    });

                    ret.totalItems = auditType.page?.totalItems;
                    ret.currentPage = request.currentPage;
                    ret.itemsPerPage = request.itemsPerPage;
                    this._legalStatus.next(ret);
                    return ret;
                })
            );
    }

    getPreviousAuditReviewStatusList(request: any): Observable<PageResponse<ListPreviousAuditReviewStatus>> {
        let params = {};
        params = this.getParams(request, params);

        const httpOptionsLocal = { params: params };
        return this._httpClient.get<any>(this.baseUrl + 'api/data/previous-audit-review-status', httpOptionsLocal)
            .pipe(
                map((auditType: any) => {
                    const ret: PageResponse<ListPreviousAuditReviewStatus> = new PageResponse<ListPreviousAuditReviewStatus>();
                    const returnData = auditType?.items;

                    ret.items = returnData?.map((row) => {
                        return {
                            id: row.id
                            , reviewStatus: row.reviewStatus
                            , sortOrder: row.sortOrder
                        } as ListPreviousAuditReviewStatus;
                    });

                    ret.totalItems = auditType.page?.totalItems;
                    ret.currentPage = request.currentPage;
                    ret.itemsPerPage = request.itemsPerPage;
                    this._previousAuditReviewStatus.next(ret);
                    return ret;
                })
            );
    }

    getQuoteRequestStatus(request: any): Observable<PageResponse<QuoteRequestStatus>> {
        let params = {};
        params = this.getParams(request, params);

        const httpOptionsLocal = { params: params };
        return this._httpClient.get<any>(this.baseUrl + 'api/data/quote-request-status', httpOptionsLocal)
            .pipe(
                map((auditType: any) => {
                    const ret: PageResponse<QuoteRequestStatus> = new PageResponse<QuoteRequestStatus>();
                    const returnData = auditType?.items;

                    ret.items = returnData?.map((row) => {
                        return {
                            id: row.id,
                            status: row.status
                        } as QuoteRequestStatus;
                    });

                    ret.totalItems = auditType.page?.totalItems;
                    ret.currentPage = request.currentPage;
                    ret.itemsPerPage = request.itemsPerPage;
                    this._previousQuoteRequestStatus.next(ret);
                    return ret;
                })
            );
    }

    deleteOption(id: number, controller: string, prefix: string = 'data'): Observable<any> {
        return this._httpClient.delete(`${this.baseUrl}api/${prefix}/${controller}/${id}`);
    }
}
